import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const GoogleMapsContext = createContext(null);

const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry'];

/**
 * Google Maps loading context
 * Starts loading Google Maps immediately but non-blocking
 * Pages render immediately and features become available when Maps loads
 */
export function GoogleMapsProvider({ children, apiKey }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const loadPromiseRef = useRef(null);

  // Check if already loaded and start loading immediately
  useEffect(() => {
    if (window.google?.maps?.places) {
      setIsLoaded(true);
    } else {
      // Start loading Google Maps immediately (non-blocking)
      loadGoogleMapsInternal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadGoogleMapsInternal = useCallback(() => {
    // Already loaded
    if (window.google?.maps?.places) {
      setIsLoaded(true);
      return Promise.resolve();
    }

    // Return existing promise if already loading
    if (loadPromiseRef.current) {
      return loadPromiseRef.current;
    }

    setIsLoading(true);

    const promise = new Promise((resolve, reject) => {
      // Create callback function name
      const callbackName = `initGoogleMaps_${Date.now()}`;

      window[callbackName] = () => {
        setIsLoaded(true);
        setIsLoading(false);
        loadPromiseRef.current = null;
        delete window[callbackName];
        resolve();
      };

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${GOOGLE_MAPS_LIBRARIES.join(',')}&callback=${callbackName}&loading=async`;
      script.async = true;
      script.defer = true;

      script.onerror = (error) => {
        setLoadError(error);
        setIsLoading(false);
        loadPromiseRef.current = null;
        delete window[callbackName];
        reject(error);
      };

      document.head.appendChild(script);
    });

    loadPromiseRef.current = promise;
    return promise;
  }, [apiKey]);

  // Public method to trigger loading (will reuse existing promise if already loading)
  const loadGoogleMaps = useCallback(() => {
    return loadGoogleMapsInternal();
  }, [loadGoogleMapsInternal]);

  const value = {
    isLoaded,
    isLoading,
    loadError,
    loadGoogleMaps,
  };

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  );
}

/**
 * Hook to access Google Maps loading state and trigger loading
 */
export function useGoogleMaps() {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
  }
  return context;
}

/**
 * HOC wrapper that ensures Google Maps is loaded before rendering component
 * Shows a loading state while Google Maps loads
 */
export function withGoogleMaps(WrappedComponent, LoadingComponent = null) {
  return function GoogleMapsWrapper(props) {
    const { isLoaded, isLoading, loadError, loadGoogleMaps } = useGoogleMaps();

    useEffect(() => {
      if (!isLoaded && !isLoading) {
        loadGoogleMaps();
      }
    }, [isLoaded, isLoading, loadGoogleMaps]);

    if (loadError) {
      return (
        <div className="loading" style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Error Loading Maps</h2>
          <p>Failed to load Google Maps. Please refresh the page.</p>
        </div>
      );
    }

    if (!isLoaded) {
      if (LoadingComponent) {
        return <LoadingComponent />;
      }
      return <div className="loading">Loading Maps...</div>;
    }

    return <WrappedComponent {...props} />;
  };
}

export default GoogleMapsContext;
